// Reads the guardian app's rotating pass from camera frames. The native BarcodeDetector (Chrome)
// costs nothing to ship; jsQR serves WebKit (the iPad shell) and every Android WebView app (PMU-284),
// and is only downloaded where it is used: 257 KB source, about 45 KB gzip
// once minified, in its own chunk. Rejected: @zxing/library (about 3x the size, and it decodes
// a dozen formats the kiosk never sees) and qr-scanner (spawns a blob: worker for the same job).

export type QrDecoder = (video: HTMLVideoElement) => Promise<string | null>;

interface NativeBarcodeDetector { detect(src: CanvasImageSource): Promise<{ rawValue: string }[]> }
interface NativeBarcodeDetectorCtor {
  new (o: { formats: string[] }): NativeBarcodeDetector;
  getSupportedFormats?: () => Promise<string[]>;
}

/**
 * Never the native detector inside an Android WebView (the parent, staff and door apps): the WebView
 * binds Google Play Services for it, and on a phone without them and an older WebView that throws
 * on its GPU thread and kills the whole app, which no JS can catch (PMU-284; seen on WebView 124
 * without Play Services). jsQR reads a phone or tablet screen as fast at half resolution.
 */
export function nativeQrSafe(ua: string = globalThis.navigator?.userAgent ?? ""): boolean {
  return !(/Android/.test(ua) && /; wv\)/.test(ua));
}

export async function makeQrDecoder(): Promise<QrDecoder> {
  const Native = nativeQrSafe() ? (globalThis as { BarcodeDetector?: NativeBarcodeDetectorCtor }).BarcodeDetector : undefined;
  if (Native) {
    try {
      const formats = (await Native.getSupportedFormats?.()) ?? ["qr_code"];
      if (formats.includes("qr_code")) {
        const det = new Native({ formats: ["qr_code"] });
        return async (video) => (await det.detect(video).catch(() => []))[0]?.rawValue ?? null;
      }
    } catch { /* fall through to jsQR */ }
  }
  const { default: jsQR } = await import("jsqr");
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  return async (video) => {
    const vw = video.videoWidth, vh = video.videoHeight;
    if (!ctx || !vw || !vh) return null;
    // Half resolution is plenty for a phone screen held at the tablet, and 4x cheaper.
    const w = Math.round(vw / 2), h = Math.round(vh / 2);
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(video, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    return jsQR(img.data, w, h, { inversionAttempts: "dontInvert" })?.data ?? null;
  };
}

/** Friendly text for a pass that did not verify; the adult is never blamed. */
export function passProblem(reason: string): string {
  if (/rotated/i.test(reason)) return "That code has just changed. Please show the new code on your phone.";
  if (/expired/i.test(reason)) return "This pass has ended. Please find your child by name instead.";
  if (/another site/i.test(reason)) return "That pass is for a different school or center.";
  return "That code could not be read as a pass. Please try again or tap a name instead.";
}
